import { EXAM_COURSE_ROUTES } from "./examCourseRoutes";
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import HomeDashboardLayout from '../HomeDashboardLayout';
import SubjectCategorySlider from '../../SubjectCategorySlider';
import QuestionBankSubjects from '../../QuestionBankSubjects';
import { useExamSubjectCategories } from '../../hooks/useExamSubjectCategories';
import { useQuestionBankSubjects } from '../../hooks/useQuestionBankSubjects';
import { useQuestionBankTopicToggle } from '../../hooks/useQuestionBankTopicToggle';
import { setDefaultExamSubjectCategory } from '../../services/examCategoryService';
import { buildQuestionBankFlowState } from '../../questionBank/qbankFlowState';
import { getStoredUser } from '../../utils/authStorage';
import { BackSVG } from '../../components/SvgIcons';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../DashboardFavoriteExam.css';
import '../rapidFire/RapidFireSubject.css';
import '../../DashboardQuestionBank.css';
import '../../homeDashboard/DashboardSkeleton.css';

function CategoryShimmer() {
  return <div className="cs-status">Loading subject categories...</div>;
}

export default function ExamSubjects({ categoryId: categoryOverride } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useMemo(() => location.state || {}, [location.state]);
  const storedUser = getStoredUser();
  const examId = Number(context.exams_id || context.exam_id || 0);
  const categoryId = Number(categoryOverride ?? context.category_id ?? 2) || 2;
  const stateId = Number(context.states_id || context.state_id || storedUser?.states_id || storedUser?.state_id || 0);
  const freePaid = String(context.free_paid || '').toUpperCase();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(
    () => Number(context.subject_category_id || 0) || 'all'
  );
  const [isCategoryResolved, setIsCategoryResolved] = useState(false);
  const [topicsEnabled, setTopicsEnabled] = useState(false);
  const [hasTopicToggleOverride, setHasTopicToggleOverride] = useState(false);
  const [isBannerLoaded, setIsBannerLoaded] = useState(false);

  const categoryQuery = useExamSubjectCategories(examId, categoryId, freePaid, Boolean(examId));
  const categories = useMemo(
    () => categoryQuery.data?.subjectCategoryList || [],
    [categoryQuery.data]
  );
  const defaultCategory = categories.find((item) => Number(item?.is_default) === 1);
  const saveCategoryMutation = useMutation({ mutationFn: setDefaultExamSubjectCategory });

  useEffect(() => {
    // Demo and Course Content use the same route. Discard the previous tab's
    // search, page, and selected category before its access-specific data loads.
    setSearchTerm('');
    setCurrentPage(1);
    setSelectedCategory(Number(context.subject_category_id || 0) || 'all');
    setIsCategoryResolved(false);
    setTopicsEnabled(false);
    setHasTopicToggleOverride(false);
  }, [categoryId, context.subject_category_id, examId, freePaid, location.key]);

  useEffect(() => {
    if (categoryQuery.isLoading || categoryQuery.isFetching) return;
    setSelectedCategory((current) => {
      if (current !== 'all' && categories.some((item) => Number(item.subject_category_id) === Number(current))) {
        return current;
      }
      return defaultCategory ? Number(defaultCategory.subject_category_id) : 'all';
    });
    setIsCategoryResolved(true);
  }, [categories, categoryQuery.isFetching, categoryQuery.isLoading, defaultCategory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const subjectParams = useMemo(() => ({
    page: currentPage,
    states_id: stateId,
    exams_id: examId,
    category_id: categoryId,
    free_paid: freePaid || undefined,
    search_query: searchTerm.trim() || undefined,
    subject_category_id: selectedCategory === 'all' ? undefined : Number(selectedCategory),
  }), [categoryId, currentPage, examId, freePaid, searchTerm, selectedCategory, stateId]);

  const subjectsQuery = useQuestionBankSubjects(subjectParams, {
    enabled: Boolean(examId && stateId && isCategoryResolved && !categoryQuery.isFetching),
    examFlow: true,
  });
  const topicToggleMutation = useQuestionBankTopicToggle(subjectParams);
  const subjectData = subjectsQuery.data || {};
  const categoryName = subjectData.categoryName || '';
  const isCategoryNameLoading = !categoryName && !subjectsQuery.isError;
  const heroImage = subjectData.bannerImage || '';
  const shouldShowBannerShimmer = heroImage
    ? !isBannerLoaded
    : subjectsQuery.isLoading || subjectsQuery.isFetching;
  const isTopicToggleVisible =
    freePaid !== 'F' && Number(subjectData.topicVisibilityStatus) === 1;

  useEffect(() => {
    if (!hasTopicToggleOverride) {
      setTopicsEnabled(Number(subjectData.topicToggleStatus) === 1);
    }
  }, [hasTopicToggleOverride, subjectData.topicToggleStatus]);

  useEffect(() => {
    setIsBannerLoaded(false);
  }, [heroImage]);

  const navigationState = useMemo(() => buildQuestionBankFlowState({
    ...context,
    examFlow: true,
    exams_id: examId,
    exam_id: examId,
    free_paid: freePaid,
    state_id: stateId,
    category_id: categoryId,
    subjectsPath: EXAM_COURSE_ROUTES.subjects,
    selectedState: { state_id: stateId, state_name: subjectData.stateName || context.state_name || '' },
    selectedCategory: { category_id: categoryId, category_name: subjectData.categoryName || 'Question Bank' },
    freePaidResources: subjectData.isSubscribed,
    plan_duration_id: Number(context.plan_duration_id || context.planDurationId || 0),
    exam_plan_id: Number(context.exam_plan_id || context.examPlanId || 0),
  }), [categoryId, context, examId, freePaid, stateId, subjectData.categoryName, subjectData.isSubscribed, subjectData.stateName]);

  const handleTopicToggle = async (event) => {
    const nextValue = event.target.checked;
    setTopicsEnabled(nextValue);
    setHasTopicToggleOverride(true);
    try {
      await topicToggleMutation.mutateAsync({
        category_id: categoryId,
        topic_toogle_status: nextValue ? 1 : 0,
        exam_id: examId,
      });
    } catch (_error) {
      setTopicsEnabled(!nextValue);
      setHasTopicToggleOverride(false);
    }
  };

  return (
    <HomeDashboardLayout showHeader={false}>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
        .layout-wrapper.homeDashboard-wrapper {
          background: linear-gradient(to bottom, rgba(103, 78, 221, 0.05) 0%, #fff 100%) !important;
        }
       .qb-topics-row {
           margin-bottom: 25px;
        }
        @media (max-width: 1600px){
         .qb-topics-row {
           margin-bottom: 0px;
          }
        }
        @media (max-width: 480px){
         .qb-topics-row {
           margin-bottom: 15px;
          }
        }
      `}</style>
      <section className="qb exam-subjects-page" aria-label="Exam question bank subjects">
        <div className="d-flex align-items-center justify-content-between">
          <button
            type="button"
            className="languageImg d-flex xl-invisible doubt-back"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <BackSVG />
          </button>
        </div>
        <div className="qb-body">
          <div className="d-flex align-items-center justify-content-center">
            <div className="qb-copy">
              <div className="qb-loc"><i className="bi bi-geo-alt-fill" /> <span>{subjectData.stateName || context.state_name || 'Selected state'}</span></div>
              <h1 className="qb-title">
                {isCategoryNameLoading ? (
                  <span className="dashboard-skeleton dashboard-skeleton--line exam-subjects-title-shimmer" aria-label="Loading category name" />
                ) : (
                  <span>{categoryName || 'Exam Content'}</span>
                )}
              </h1>
              <p className="qb-sub">{context.exam_name || subjectData.categoryName || 'Exam course'}</p>
              {isTopicToggleVisible ? (
                <div className="qb-topics-row">
                  <div className="qb-topics">
                    <span className="qb-topics-label">Topics</span>
                    <label className={`qb-toggle ${topicsEnabled ? 'is-on' : ''}`}>
                      <input type="checkbox" checked={topicsEnabled} onChange={handleTopicToggle} disabled={topicToggleMutation.isPending} aria-label="Toggle topics" />
                      <span className="qb-toggle-track"><span className="qb-toggle-thumb" /></span>
                    </label>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="qb-visual" aria-hidden="true">
              <div className="qb-dots">
                {Array.from({ length: 21 }).map((_, index) => <span key={index} />)}
              </div>
              {shouldShowBannerShimmer ? <span className="dashboard-skeleton exam-subjects-banner-shimmer" /> : null}
              {heroImage ? (
                <img
                  src={heroImage}
                  alt=""
                  className="qb-hero"
                  onLoad={() => setIsBannerLoaded(true)}
                  onError={() => setIsBannerLoaded(true)}
                  style={{ opacity: isBannerLoaded ? 1 : 0 }}
                />
              ) : null}
            </div>
          </div>
          <label className="qb-search" aria-label="Search subjects">
            <i className="bi bi-search" />
            <input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search subjects" />
          </label>
        </div>
      </section>
      <div className="innerContent dashboardExam">
        {categoryQuery.isLoading ? <CategoryShimmer /> : null}
        {categoryQuery.isError ? <div className="cs-status">Unable to load subject categories.</div> : null}
        {!categoryQuery.isLoading && !categoryQuery.isError && categories.length ? (
          <>
            <div className="quick-action__header"><h2 className="quick-action__title">Subject Categories</h2></div>
            <SubjectCategorySlider
              categories={categories}
              allSubjectCount={categoryQuery.data?.allSubjectCount || 0}
              activeSubject={selectedCategory}
              onSelect={setSelectedCategory}
              persistSelection={false}
              onPersistSelection={(subjectCategoryId) => saveCategoryMutation.mutate({ subject_category_id: subjectCategoryId })}
            />
          </>
        ) : null}
        <div className="m-20">
          {subjectsQuery.isError ? <div className="cs-status">{subjectsQuery.error?.response?.data?.message || 'Unable to load exam subjects.'}</div> : null}
          {!subjectsQuery.isError ? (
            <QuestionBankSubjects
              subjects={subjectData.subjectList || []}
              searchTerm={searchTerm}
              requestParams={subjectParams}
              pagination={subjectData.pagination || {}}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              isLoading={subjectsQuery.isLoading || subjectsQuery.isFetching}
              navigationState={navigationState}
              topicsEnabled={topicsEnabled}
              isExamCourseFlow
            />
          ) : null}
        </div>
      </div>
    </HomeDashboardLayout>
  );
}
